import { useEffect, useRef, type ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { esCount } from '@/lib/es-count';
import { formatNumber } from '@/lib/format-es';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';

export interface ResourceIndexColumn<T> {
    id: string;
    header: string;
    align?: 'left' | 'right';
    sortable?: boolean;
    cell: (row: T) => ReactNode;
}

export interface ResourceIndexView {
    id: string;
    label: string;
    count?: number;
}

export interface ResourceIndexChip {
    id: string;
    label: string;
}

export interface ResourceIndexBulkAction {
    id: string;
    label: string;
    disabled?: boolean;
    /** Se muestra al pasar el ratón cuando la acción no se puede ejecutar. */
    disabledReason?: string;
    onClick: () => void;
}

export interface ResourceIndexProps<T> {
    title?: string;
    description?: string;
    actions?: ReactNode;
    views: ResourceIndexView[];
    view: string;
    onViewChange: (id: string) => void;
    search: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder?: string;
    filters?: ReactNode;
    chips: ResourceIndexChip[];
    onRemoveChip: (id: string) => void;
    columns: ResourceIndexColumn<T>[];
    rows: T[];
    rowId: (row: T) => string;
    sort: string | null;
    sortDir: 'asc' | 'desc';
    onSort: (columnId: string) => void;
    selectedIds: readonly string[];
    onToggleRow: (id: string) => void;
    onTogglePage: (checked: boolean) => void;
    onSelectView?: () => void;
    selectViewPending?: boolean;
    bulkActions?: ResourceIndexBulkAction[];
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    isLoading?: boolean;
    error?: string | null;
    emptyTitle: string;
    emptyDescription?: string;
    onRowClick?: (row: T) => void;
}

export function ResourceIndex<T>({
    title,
    description,
    actions,
    views,
    view,
    onViewChange,
    search,
    onSearchChange,
    searchPlaceholder = 'Buscar…',
    filters,
    chips,
    onRemoveChip,
    columns,
    rows,
    rowId,
    sort,
    sortDir,
    onSort,
    selectedIds,
    onToggleRow,
    onTogglePage,
    onSelectView,
    selectViewPending = false,
    bulkActions = [],
    page,
    pageSize,
    total,
    onPageChange,
    isLoading = false,
    error,
    emptyTitle,
    emptyDescription,
    onRowClick,
}: ResourceIndexProps<T>) {
    const pageIds = rows.map(rowId);
    const selected = new Set(selectedIds);
    const pageHits = pageIds.filter((id) => selected.has(id)).length;
    const pageAll = pageIds.length > 0 && pageHits === pageIds.length;
    const pageSome = pageHits > 0 && pageHits < pageIds.length;
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div className="space-y-4">
            {(title || description || actions) && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        {title ? <h2 className="text-lg font-semibold text-foreground">{title}</h2> : null}
                        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
                    </div>
                    {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
                </div>
            )}

            <div
                role="tablist"
                aria-label="Vistas"
                className="flex gap-1 p-1 bg-muted/50 rounded-lg overflow-x-auto max-w-full"
            >
                {views.map((item) => {
                    const active = item.id === view;
                    return (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => onViewChange(item.id)}
                            className={cn(
                                'px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                                active
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {item.label}
                            {item.count != null ? (
                                <span className="ml-1.5 text-[10px] opacity-70">({formatNumber(item.count)})</span>
                            ) : null}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="relative flex-1 min-w-0 max-w-sm">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={searchPlaceholder}
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                </div>
                {filters ? <div className="flex flex-wrap items-center gap-2">{filters}</div> : null}
            </div>

            {chips.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {chips.map((chip) => (
                        <button
                            key={chip.id}
                            type="button"
                            onClick={() => onRemoveChip(chip.id)}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs text-foreground hover:bg-muted"
                        >
                            {chip.label}
                            <X size={12} className="text-muted-foreground" />
                            <span className="sr-only">Quitar filtro</span>
                        </button>
                    ))}
                </div>
            ) : null}

            {selectedIds.length > 0 ? (
                <div className="flex flex-col gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium text-foreground">
                            {esCount(selectedIds.length, 'seleccionado')}
                        </span>
                        {onSelectView ? (
                            <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto px-0"
                                disabled={selectViewPending || selectedIds.length >= total}
                                onClick={onSelectView}
                            >
                                {selectViewPending
                                    ? 'Seleccionando…'
                                    : total === 1
                                      ? 'Seleccionar el de esta vista'
                                      : `Seleccionar los ${formatNumber(total)} de esta vista`}
                            </Button>
                        ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <TooltipProvider delayDuration={200}>
                            {bulkActions.map((action) => {
                                const button = (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        disabled={action.disabled}
                                        onClick={action.onClick}
                                    >
                                        {action.label}
                                    </Button>
                                );
                                if (!action.disabled || !action.disabledReason) {
                                    return <span key={action.id}>{button}</span>;
                                }
                                return (
                                    <Tooltip key={action.id}>
                                        <TooltipTrigger asChild>
                                            <span className="inline-flex">{button}</span>
                                        </TooltipTrigger>
                                        <TooltipContent>{action.disabledReason}</TooltipContent>
                                    </Tooltip>
                                );
                            })}
                        </TooltipProvider>
                    </div>
                </div>
            ) : null}

            {error ? <p className="text-sm text-red-400">{error}</p> : null}

            {isLoading ? (
                <div className="space-y-2">
                    {Array.from({ length: 6 }).map((_, index) => (
                        <div key={index} className="h-16 rounded-xl border border-border bg-card animate-pulse" />
                    ))}
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center">
                    <p className="text-sm font-medium text-muted-foreground">{emptyTitle}</p>
                    {emptyDescription ? (
                        <p className="mt-1 text-xs text-muted-foreground/80">{emptyDescription}</p>
                    ) : null}
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-border bg-card">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                                <th className="w-10 px-3 py-2.5">
                                    <PageCheckbox
                                        all={pageAll}
                                        some={pageSome}
                                        onChange={onTogglePage}
                                        label="Seleccionar las filas de esta página"
                                    />
                                </th>
                                {columns.map((column) => {
                                    const active = sort === column.id;
                                    const align = column.align === 'right' ? 'text-right' : 'text-left';
                                    return (
                                        <th key={column.id} className={cn('px-4 py-2.5 font-medium', align)}>
                                            {column.sortable === false ? (
                                                column.header
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => onSort(column.id)}
                                                    className={cn(
                                                        'inline-flex items-center gap-1 hover:text-foreground',
                                                        align === 'text-right' && 'flex-row-reverse',
                                                        active && 'text-foreground',
                                                    )}
                                                >
                                                    {column.header}
                                                    {active ? (sortDir === 'asc' ? '↑' : '↓') : null}
                                                </button>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const id = rowId(row);
                                return (
                                    <tr
                                        key={id}
                                        onClick={() => onRowClick?.(row)}
                                        className={cn(
                                            'border-b last:border-0 border-border transition-colors',
                                            onRowClick && 'cursor-pointer hover:bg-muted/30',
                                            selected.has(id) && 'bg-primary/5',
                                        )}
                                    >
                                        <td
                                            className="px-3 py-3"
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected.has(id)}
                                                aria-label="Seleccionar fila"
                                                onChange={() => onToggleRow(id)}
                                                className="h-4 w-4 accent-primary"
                                            />
                                        </td>
                                        {columns.map((column) => (
                                            <td
                                                key={column.id}
                                                className={cn(
                                                    'px-4 py-3',
                                                    column.align === 'right' && 'text-right tabular-nums',
                                                )}
                                            >
                                                {column.cell(row)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span>
                    {from === 0
                        ? '0 resultados'
                        : `${formatNumber(from)}–${formatNumber(to)} de ${formatNumber(total)}`}
                </span>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => onPageChange(page - 1)}
                    >
                        Anterior
                    </Button>
                    <span>
                        {formatNumber(page)} / {formatNumber(pageCount)}
                    </span>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={page >= pageCount}
                        onClick={() => onPageChange(page + 1)}
                    >
                        Siguiente
                    </Button>
                </div>
            </div>
        </div>
    );
}

function PageCheckbox({
    all,
    some,
    onChange,
    label,
}: {
    all: boolean;
    some: boolean;
    onChange: (checked: boolean) => void;
    label: string;
}) {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = some;
    }, [some]);
    return (
        <input
            ref={ref}
            type="checkbox"
            checked={all}
            aria-label={label}
            onChange={(event) => onChange(event.target.checked)}
            className="h-4 w-4 accent-primary"
        />
    );
}
